// Signaling op:get / op:set plumbing for the settings page (common/04 op model,
// spec 21 §2). All wire frames go through the shared ws.ts singleton:
//   - attr read  → signaling.sendCmd(deviceId, 'get', id)  → report frame
//   - attr write → signaling.sendCmd(deviceId, 'set', id, { boolVal|intVal|strVal })
//   - CMD (0x3-type ids) → op:set per GAP-DS-7 working assumption (spec 21 §2.3/§2.5)
//
// Optional members (Sensitivity / ConfidenceThreshold / CurrentLevel, common/04 §11)
// are probed with op:get on entry; timeout/error → the widget hides itself
// (spec 21 §2.1 探測, GAP-DS-5).

import { useCallback, useEffect, useRef, useState } from 'react';
import { signaling } from '../../../lib/ws';
import type { SignalFrame, SignalingState } from '../../../lib/ws';
import i18n from '../../../i18n';
import { revokedTombstones } from '../revokedTombstones';
import { WIFI_RECONFIGURE, WIFI_SCAN, WIFI_SSID } from './ids';

export type OpValue = boolean | number | string;

const VALUE_KEYS = ['boolVal', 'intVal', 'enumVal', 'strVal', 'value'] as const;

/** Extract the typed value from a report frame (top-level first, then payload). */
export function frameValue(frame: SignalFrame): OpValue | undefined {
  const sources: Array<Record<string, unknown> | undefined> = [frame as Record<string, unknown>, frame.payload];
  for (const src of sources) {
    if (!src) continue;
    for (const k of VALUE_KEYS) {
      const v = src[k];
      if (typeof v === 'boolean' || typeof v === 'number' || typeof v === 'string') return v;
    }
  }
  return undefined;
}

/** Extract a structured rawVal from a report. JSON-string legacy bridges are tolerated. */
export function frameRawValue(frame: SignalFrame): unknown {
  const sources: Array<Record<string, unknown> | undefined> = [frame as Record<string, unknown>, frame.payload];
  for (const src of sources) {
    if (!src || !('rawVal' in src)) continue;
    const raw = src.rawVal;
    if (typeof raw === 'string') {
      try { return JSON.parse(raw); } catch { return raw; }
    }
    return raw;
  }
  return undefined;
}

/** Value → wire field name (common/04 §5 wire examples use boolVal/intVal/strVal). */
function valueField(v: OpValue): 'boolVal' | 'intVal' | 'strVal' {
  if (typeof v === 'boolean') return 'boolVal';
  if (typeof v === 'number') return 'intVal';
  return 'strVal';
}

/** op:set an attr value. Resolves on the correlated device report (ACK). */
export function opSet(deviceId: string, id: string, value: OpValue, channel?: number): Promise<SignalFrame> {
  return signaling.sendCmd(deviceId, 'set', id, { [valueField(value)]: value, ...(channel !== undefined ? { channel } : {}) });
}

/**
 * op:set an ENUM attr (wire field `enumVal`, ADR-046). The three record-settings
 * attrs (0x8050 mode/location/quality) are enum-typed and signaling's
 * recsettings handler REQUIRES `enumVal` — the generic opSet's `intVal` is
 * rejected. Resolves on the correlated device report (ACK).
 */
export function opSetEnum(deviceId: string, id: string, n: number, channel?: number): Promise<SignalFrame> {
  return signaling.sendCmd(deviceId, 'set', id, { enumVal: n, ...(channel !== undefined ? { channel } : {}) });
}

/** op:set a raw/composite attr using the canonical rawVal field (ADR-054 schedules). */
export function opSetRaw(deviceId: string, id: string, rawVal: Record<string, unknown>, channel?: number): Promise<SignalFrame> {
  return signaling.sendCmd(deviceId, 'set', id, { rawVal, ...(channel !== undefined ? { channel } : {}) });
}

/** op:get an attr; resolves with the report frame. */
export function opGet(deviceId: string, id: string, channel?: number): Promise<SignalFrame> {
  return signaling.sendCmd(deviceId, 'get', id, channel !== undefined ? { channel } : undefined);
}

/**
 * Invoke a CMD (0x3-type id). Working assumption per GAP-DS-7 (spec 21 §2.3):
 * op:set on the CMD id, optional strVal/intVal argument, ACK = report with the
 * same request_id.
 */
export function opCmd(deviceId: string, id: string, arg?: OpValue): Promise<SignalFrame> {
  return signaling.sendCmd(deviceId, 'set', id, arg !== undefined ? { [valueField(arg)]: arg } : undefined);
}

// ---- Wi-Fi 重配 · 模式 A（device/09 §3；走 WSS op:set + 0x31 CMD id + rawVal；保綁定；owner-only） ----

export type WifiSecurity = 'open' | 'wpa2' | 'wpa3' | 'wpa2wpa3' | 'wep';

export interface WifiCredentials {
  ssid: string;
  psk?: string; // open 網路可省略；敏感值，勿記錄/log
  bssid?: string; // 選填，指定 AP（多 AP/漫遊）
  security?: WifiSecurity; // 省略＝device 依 scan 自動判定
}

export interface WifiAp {
  ssid: string;
  rssi: number;
  security?: string;
  band?: string;
}

/**
 * 模式 A 前置（device/09 §3.0）：請 device 掃描並回報「它看得到的」可見網路，App 由此讓
 * 使用者選 SSID（wifi 來源由 device 提供，不從 DB，WRC-5）。report.rawVal＝{aps:[…]}（§8.1.1）。
 */
/**
 * 同一 SSID 常被多個 radio/BSSID(2.4G+5G、mesh)回報多筆 → 依 SSID 去重、留 RSSI 最強
 *(換網選的是 SSID 非 BSSID);依訊號由強到弱排序;濾掉空 SSID(隱藏網路走手動輸入)。
 */
export function dedupeAps(aps: WifiAp[]): WifiAp[] {
  const best = new Map<string, WifiAp>();
  for (const ap of aps) {
    if (!ap || typeof ap.ssid !== 'string' || ap.ssid === '') continue;
    const e = best.get(ap.ssid);
    if (!e || (ap.rssi ?? -999) > (e.rssi ?? -999)) best.set(ap.ssid, ap);
  }
  return [...best.values()].sort((a, b) => (b.rssi ?? -999) - (a.rssi ?? -999));
}

export async function opWifiScan(
  deviceId: string,
  opts?: { band?: '2.4' | '5' | 'both'; includeHidden?: boolean },
): Promise<WifiAp[]> {
  const rawVal: Record<string, unknown> = {};
  if (opts?.band) rawVal.band = opts.band;
  if (opts?.includeHidden) rawVal.include_hidden = true;
  const frame = await opSetRaw(deviceId, WIFI_SCAN, rawVal);
  const raw = frameRawValue(frame) as { aps?: unknown } | undefined;
  return dedupeAps(Array.isArray(raw?.aps) ? (raw.aps as WifiAp[]) : []);
}

/**
 * 模式 A 主命令（device/09 §3.1）：更換裝置連線的 Wi-Fi（保綁定）。
 * ⚠ resolve（device 回 report status:ok）僅表示「受理與 payload 合法」，**非切換成功** —— 切網必
 * 斷線，真正結果走 wifi.reconfig.succeeded/failed 事件 + 輪詢 readWifiSSID/presence（§3.2）。
 * 呼叫端**不得**把此 Promise 當成功。psk 敏感，勿記錄。
 */
export function opWifiReconfigure(deviceId: string, cred: WifiCredentials): Promise<SignalFrame> {
  const rawVal: Record<string, unknown> = { ssid: cred.ssid };
  if (cred.psk !== undefined) rawVal.psk = cred.psk;
  if (cred.bssid) rawVal.bssid = cred.bssid;
  if (cred.security) rawVal.security = cred.security;
  return opSetRaw(deviceId, WIFI_RECONFIGURE, rawVal);
}

/** 讀目前連線的 Wi-Fi SSID（STAT str，device/09 §8.1）；用於判定重配是否生效。 */
export async function readWifiSSID(deviceId: string): Promise<string | undefined> {
  const frame = await opGet(deviceId, WIFI_SSID);
  const v = frameValue(frame);
  return typeof v === 'string' ? v : undefined;
}

/** LS first-party unbind: WSS ingress; third-party integrations retain REST. */
export async function unbindDevice(deviceId: string): Promise<SignalFrame> {
  const frame = await signaling.request('unbind', undefined, { device_id: deviceId });
  // 自發解綁與被撤銷同病(APP#123):成功後的清單 reload 讀 stale GSI 也會讓它復活。
  // 進 tombstone;若同 session 稍後收到自己的 device_unbound frame,add 冪等。
  revokedTombstones.add(deviceId);
  return frame;
}

// ---- device room lifecycle ----

/**
 * Connect the shared signaling socket and join the device room. Re-subscribes
 * after every reconnect ('open' transition — caller's job per ws.ts contract).
 * Leaves the socket open on unmount (other pages share the singleton); only
 * unsubscribes from the room.
 */
export function useDeviceRoom(deviceId: string | undefined): SignalingState {
  const [wsState, setWsState] = useState<SignalingState>(signaling.getState());

  useEffect(() => {
    if (!deviceId) return;
    signaling.connect();
    setWsState(signaling.getState());
    const off = signaling.on('state', (s) => {
      setWsState(s);
      if (s === 'open') {
        void signaling.subscribe(deviceId).catch(() => {});
      }
    });
    if (signaling.getState() === 'open') {
      void signaling.subscribe(deviceId).catch(() => {});
    }
    return () => {
      off();
      if (signaling.getState() === 'open') {
        void signaling.unsubscribe(deviceId).catch(() => {});
      }
    };
  }, [deviceId]);

  return wsState;
}

// ---- per-attr state hook ----

export type AttrStatus = 'idle' | 'loading' | 'ready' | 'unavailable';

export interface AttrHandle {
  status: AttrStatus;
  value: OpValue | undefined;
  /** True while an op:set is in flight (optimistic value already shown). */
  setting: boolean;
  /** Last op:set failure message (cleared on next attempt). */
  setError: string | null;
  refresh: () => void;
  /** Optimistic op:set with rollback on failure (spec 21 §2.3 / 10 §2). */
  set: (v: OpValue) => void;
}

export function useAttr(deviceId: string | undefined, id: string, enabled: boolean, channel?: number): AttrHandle {
  const [status, setStatus] = useState<AttrStatus>('idle');
  const [value, setValue] = useState<OpValue | undefined>(undefined);
  const [setting, setSetting] = useState(false);
  const [setError, setSetErr] = useState<string | null>(null);
  const alive = useRef(true);
  const fetched = useRef(false);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const refresh = useCallback(() => {
    if (!deviceId) return;
    setStatus('loading');
    opGet(deviceId, id, channel).then(
      (f) => {
        if (!alive.current) return;
        setValue(frameValue(f));
        setStatus('ready');
      },
      () => {
        if (!alive.current) return;
        setStatus('unavailable');
      }
    );
  }, [deviceId, id, channel]);

  useEffect(() => {
    if (enabled && deviceId && !fetched.current) {
      fetched.current = true;
      refresh();
    }
  }, [enabled, deviceId, refresh]);

  // 訂閱 device 主動廣播的屬性變更 report(無 request_id → 走 'event' 通道;common/04 §5.11、ADR-046)。
  // 例:換網完成後 device 主動送 WifiSSID report → 「目前連線」即時更新,免手動 refresh。
  // 只認同 id、type:'report'、且帶值的(空 report 不覆寫既有值)。
  useEffect(() => {
    if (!enabled || !deviceId) return;
    const off = signaling.on('event', (f: SignalFrame) => {
      if (f.type !== 'report' || f.device_id !== deviceId) return;
      const p = (f.payload ?? f) as Record<string, unknown>;
      const fid = String(f.id ?? p.id ?? '').toUpperCase();
      if (fid !== id.toUpperCase()) return;
      const v = frameValue(f);
      if (v !== undefined) {
        setValue(v);
        setStatus('ready');
      }
    });
    return off;
  }, [enabled, deviceId, id]);

  const set = useCallback(
    (v: OpValue) => {
      if (!deviceId) return;
      const prev = value;
      setSetErr(null);
      setValue(v); // optimistic
      setSetting(true);
      opSet(deviceId, id, v, channel).then(
        () => {
          if (!alive.current) return;
          setSetting(false);
        },
        (err: unknown) => {
          if (!alive.current) return;
          setSetting(false);
          setValue(prev); // rollback
          setSetErr(err instanceof Error ? err.message : i18n.t('deviceSettings.setFailed'));
        }
      );
    },
    [deviceId, id, value, channel]
  );

  return { status, value, setting, setError, refresh, set };
}
